import {
  SERVICE_FEE_PER_ITEM,
  ADDITIONAL_LOCATION_FEE,
  POSTAL_FEE,
  NATIONWIDE_MINIMUM_FEE,
  NATIONWIDE_MINIMUM_INCLUDED_ITEMS,
} from "../constants";
import { getCostByDocumentId } from "./documentTree";

/**
 * 料金計算の入力パラメータ。
 */
export interface EstimateInput {
  /** 選択済み書類 ID の配列 */
  selectedDocuments: string[];
  /** その他書類（フリー入力）の配列 */
  otherDocuments: string[];
  /** ヒアリング項目の配列 */
  hearingItems: string[];
  /** 書類ごとの取得拠点マップ (書類 ID → 拠点名) */
  documentLocations: Record<string, string>;
  /** 郵送希望フラグ */
  needsPostalDelivery: boolean;
  /** エリア種別 */
  areaType: "tokyo_metro" | "nationwide";
}

/**
 * 料金計算の結果。
 */
export interface EstimatedCost {
  /** 実費（書類取得手数料）合計 */
  actualCostTotal: number;
  /** サービス利用手数料合計 */
  serviceFeeTotal: number;
  /** 拠点追加費用 */
  locationFee: number;
  /** 郵送費用 */
  postalFee: number;
  /** 総合計費用 */
  total: number;
  /** 選択書類＋その他書類の件数（空文字は除く） */
  documentItemCount: number;
  /** ヒアリング項目の件数（空文字は除く） */
  hearingItemCount: number;
  /** サービス利用手数料の対象件数合計（documentItemCount + hearingItemCount） */
  totalItemCount: number;
}

/**
 * 役所資料取得代行の概算費用を計算する純粋関数。
 * 地番・書類選択・エリア種別・郵送希望を元に費用内訳を返す。
 *
 * @param input 計算に必要な入力パラメータ
 * @returns 費用内訳オブジェクト
 */
export const calculateEstimatedCost = (input: EstimateInput): EstimatedCost => {
  const {
    selectedDocuments,
    otherDocuments,
    hearingItems,
    documentLocations,
    needsPostalDelivery,
    areaType,
  } = input;

  // 実費（書類取得手数料）合計
  let actualCostTotal = 0;
  selectedDocuments.forEach((docId) => {
    actualCostTotal += getCostByDocumentId(docId);
  });

  // サービス利用手数料合計
  const otherDocumentCount = otherDocuments.filter(
    (d): d is string => typeof d === "string" && d.trim() !== ""
  ).length;
  const hearingItemCount = hearingItems.filter(
    (h): h is string => typeof h === "string" && h.trim() !== ""
  ).length;
  const documentItemCount = selectedDocuments.length + otherDocumentCount;
  const totalItemCount = documentItemCount + hearingItemCount;
  // 1 都 3 県以外は最低発注料金（NATIONWIDE_MINIMUM_INCLUDED_ITEMS 件まで
  // NATIONWIDE_MINIMUM_FEE・超過分は 1 件 SERVICE_FEE_PER_ITEM）を適用する。
  // 最低発注料金はサービス利用手数料のみに掛かり、拠点追加費用・実費・郵送とは独立する
  // （郵送費用・拠点追加費用は件数によらずユーザーの独立した入力に基づく。
  // 「郵送希望あり → postalFee = 430」テストの通り、件数0でも郵送希望があれば課金する）。
  const serviceFeeTotal =
    totalItemCount === 0
      ? 0
      : areaType === "nationwide"
      ? NATIONWIDE_MINIMUM_FEE +
        Math.max(0, totalItemCount - NATIONWIDE_MINIMUM_INCLUDED_ITEMS) *
          SERVICE_FEE_PER_ITEM
      : totalItemCount * SERVICE_FEE_PER_ITEM;

  // 拠点追加費用（1 都 3 県以外のみ、ユニーク取得先数 - 1）
  const uniqueLocations = new Set(
    Object.values(documentLocations).filter((v) => v && v.trim() !== "")
  );
  const uniqueLocationCount = Math.max(uniqueLocations.size, 1);
  const locationFee =
    areaType === "nationwide"
      ? Math.max(0, uniqueLocationCount - 1) * ADDITIONAL_LOCATION_FEE
      : 0;

  const postalFee = needsPostalDelivery ? POSTAL_FEE : 0;
  const total = actualCostTotal + serviceFeeTotal + locationFee + postalFee;

  return {
    actualCostTotal,
    serviceFeeTotal,
    locationFee,
    postalFee,
    total,
    documentItemCount,
    hearingItemCount,
    totalItemCount,
  };
};
